import { useMemo, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import FilterMenu from '@/components/generals/filter-menu'
import StatCard from '@/components/generals/stat-card'
import SegmentBar from '@/components/generals/segment-bar'
import ForecastChart, { FORECAST_SERIES } from '@/components/forecast/forecast-chart'
import { EmptyState, SectionHeading, ViewToolbar } from '@/components/generals/view-parts'
import { ownerByName } from '@/constants/companies'
import { PIPELINES, QUARTERLY_TARGET } from '@/constants/crm'
import { formatCompactMoney, formatMoney } from '@/lib/companies'
import { forecastForQuarter } from '@/lib/crm'
import { quarterFromKey, quarterOf, shiftQuarter } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'

const ALL = 'all'

const PIPELINE_OPTIONS = [
    { value: ALL, label: "All Pipelines" },
    ...PIPELINES.map(pipeline => ({ value: pipeline.label, label: pipeline.label }))
]

interface ForecastViewProps {
    initialQuarter: string
}

export default function ForecastView({ initialQuarter }: ForecastViewProps) {
    const today = useCrmStore(state => state.today)
    const status = useCrmStore(state => state.status)
    const deals = useCrmStore(state => state.deals)
    const openProfile = useCrmStore(state => state.openProfile)
    const [quarterKey, setQuarterKey] = useState(initialQuarter)
    const [pipeline, setPipeline] = useState(ALL)

    const current = quarterOf(today)
    const quarter = quarterFromKey(quarterKey) ?? current
    const quarterOptions = useMemo(() => {
        const options = Array.from({ length: 7 }, (_, index) => shiftQuarter(current, index - 2))

        if (!options.some(option => option.key === quarter.key)) options.push(quarter)

        return options.map(option => ({
            value: option.key,
            label: option.key === current.key ? `${option.label} (current)` : option.label
        }))
    }, [current, quarter])

    const forecast = useMemo(
        () => forecastForQuarter(deals.filter(deal => pipeline === ALL || deal.pipeline === pipeline), quarter),
        [deals, pipeline, quarter]
    )
    const { totals } = forecast
    const committed = totals.won + totals.commit
    const attainment = Math.min(100, Math.round((totals.won / QUARTERLY_TARGET) * 100))
    const projected = Math.round((totals.weighted / QUARTERLY_TARGET) * 100)

    return (
        <>
            <ViewToolbar>
                <div className='flex flex-wrap items-center gap-2'>
                    <FilterMenu label="Quarter" value={quarter.key} options={quarterOptions} onChange={setQuarterKey} />
                    <FilterMenu label="Pipeline" value={pipeline} options={PIPELINE_OPTIONS} onChange={setPipeline} />
                </div>
                <span className='caption-style text-subtle'>
                    Target for {quarter.label}: <span className='text-foreground tabular-nums'>${formatMoney(QUARTERLY_TARGET)}</span>
                </span>
            </ViewToolbar>

            <div className='border-border flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto border-t p-4'>
                <div className='grid grid-cols-2 gap-2 lg:grid-cols-5'>
                    <StatCard label="Closed won" value={formatCompactMoney(totals.won)} hint={`${attainment}% of target`} />
                    <StatCard label="Commit" value={formatCompactMoney(totals.commit)} hint={`${formatCompactMoney(committed)} won and committed`} />
                    <StatCard label="Best case" value={formatCompactMoney(totals.bestCase)} hint="Deals in proposal" />
                    <StatCard label="Pipeline" value={formatCompactMoney(totals.pipeline)} hint="Discovery and qualification" />
                    <StatCard
                        label="Weighted forecast"
                        value={formatCompactMoney(totals.weighted)}
                        hint={`${projected}% of target`}
                        className='col-span-2 lg:col-span-1'
                    />
                </div>

                <section className='border-border flex flex-col gap-3 rounded-xl border p-4'>
                    <SectionHeading title="Progress to target" />
                    <div className='flex flex-col gap-2'>
                        <SegmentBar percent={attainment} segments={50} tone='success' className='h-3 w-full border border-white/4 px-px' segmentClassName='h-2' trackClassName='bg-white/8' />
                        <span className='caption-style text-soft'>
                            <span className='text-foreground tabular-nums'>${formatMoney(totals.won)}</span> closed of <span className='tabular-nums'>${formatMoney(QUARTERLY_TARGET)}</span>
                        </span>
                    </div>
                </section>

                <section className='border-border flex flex-col gap-4 rounded-xl border p-4'>
                    <SectionHeading title={`${quarter.label} by month`} />
                    {forecast.deals.length > 0 ? (
                        <ForecastChart forecast={forecast} />
                    ) : (
                        <EmptyState
                            title={status === 'loading' ? "Loading forecast…" : `No deals close in ${quarter.label}`}
                            description={status === 'loading' ? undefined : "Set a close date in this quarter on any deal and it shows up here."}
                            className='py-10'
                        />
                    )}
                </section>

                <section className='border-border flex flex-col gap-3 rounded-xl border p-4'>
                    <SectionHeading title="By owner" count={forecast.owners.length} />
                    {forecast.owners.length > 0 ? (
                        <div className='-mx-4 overflow-x-auto'>
                            <table className='w-full min-w-[720px] border-collapse text-[14px] leading-none'>
                                <thead>
                                    <tr className='border-border border-b'>
                                        <th scope='col' className='caption-style text-subtle h-[38px] px-4 text-left font-normal'>Owner</th>
                                        {FORECAST_SERIES.map(series => (
                                            <th key={series.key} scope='col' className='caption-style text-subtle h-[38px] px-4 text-right font-normal'>
                                                {series.label}
                                            </th>
                                        ))}
                                        <th scope='col' className='caption-style text-subtle h-[38px] px-4 text-right font-normal'>Weighted</th>
                                        <th scope='col' className='caption-style text-subtle h-[38px] px-4 text-right font-normal'>Deals</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {forecast.owners.map(row => {
                                        const owner = ownerByName(row.owner)

                                        return (
                                            <tr key={row.owner} className='border-border border-b last:border-b-0'>
                                                <td className='px-4 py-3'>
                                                    <button
                                                        type='button'
                                                        onClick={() => openProfile(owner.name)}
                                                        className='hover:text-soft flex cursor-pointer items-center gap-2 whitespace-nowrap transition-colors duration-150'
                                                    >
                                                        <Avatar src={owner.avatar} alt='' />
                                                        {owner.name}
                                                    </button>
                                                </td>
                                                {FORECAST_SERIES.map(series => (
                                                    <td key={series.key} className='px-4 py-3 text-right tabular-nums'>
                                                        {row.totals[series.key] > 0 ? `$${formatMoney(row.totals[series.key])}` : <span className='text-subtle'>—</span>}
                                                    </td>
                                                ))}
                                                <td className='px-4 py-3 text-right font-medium tabular-nums'>${formatMoney(row.totals.weighted)}</td>
                                                <td className='px-4 py-3 text-right tabular-nums'>{row.totals.count}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <span className='caption-style text-subtle block'>
                            No owners have deals closing in {quarter.label}.
                        </span>
                    )}
                </section>
            </div>
        </>
    )
}
